/**
 * placeSearch.ts — Search orchestration: multi-vibe merge, topup, loadMore, custom vibe.
 *
 * Extracted from usePlaces.ts (H-203). Pure functions — no React dependencies.
 * Every function that issues network calls accepts an optional AbortSignal.
 */

import { getTextSearchQuery, getExcludedTypesForVibe } from '@/utils/vibeToTypes';
import type { DistanceSettings, CategorySettings } from '@/components/SettingsModal';
import {
  type Place,
  MAX_RADIUS_BY_TRANSPORT,
  performNearbySearch,
  performTextSearch,
} from '@/services/googlePlaces';
import {
  splitOpenClosed,
  getTypesForVibeFromSettings,
  getBroaderTypes,
} from '@/services/placeFilters';

// ─── Types ──────────────────────────────────────────────────────────────────

export type FindMoreStage = 0 | 1 | 2 | 3 | 4 | 5;

export interface SearchState {
  coords: { lat: number; lng: number };
  vibes: string[];
  transport: string;
  timeAvailable: string;
  location: string;
  language: string;
  distanceSettings?: DistanceSettings;
  categorySettings?: CategorySettings;
  baseRadius: number;
  currentRadius: number;
  maxRadius: number;
  radiusStep: number;
  seenPlaceIds: Set<string>;
  loadMoreAttempts: number;
  minRating: number;
  maxDistanceMeters: number;
}

export type SearchProgressCallback = (stage: 'searching' | 'ranking' | 'topup') => void;

// ─── H-204: Grid geometry ───────────────────────────────────────────────────

/** Max radius for the 2×2 grid tier. Above this, skip grid → type broadening. */
export const GRID_RADIUS_CAP_METERS = 20_000;

/**
 * Compute 3 quadrant centers around `center` for the tiled-grid Find More.
 * Q4 (SW) is omitted — the original search circle already covers it.
 *
 *     ┌─────┬─────┐
 *     │  Q2 │  Q1 │
 *     ├─────C─────┤
 *     │  Q3 │ (Q4)│
 *     └─────┴─────┘
 */
export function computeQuadrantCenters(
  center: { lat: number; lng: number },
  radiusMeters: number
): Array<{ lat: number; lng: number }> {
  const offset = radiusMeters / Math.SQRT2;
  const latOffset = offset / 111_320;
  const lngOffset = offset / (111_320 * Math.cos(center.lat * Math.PI / 180));

  return [
    { lat: center.lat + latOffset, lng: center.lng + lngOffset },  // Q1 NE
    { lat: center.lat + latOffset, lng: center.lng - lngOffset },  // Q2 NW
    { lat: center.lat - latOffset, lng: center.lng + lngOffset },  // Q3 SE
  ];
}

// ─── Internal helpers: wrap raw API + open/closed split ─────────────────────
// These mirror the original contract: filter by seenPlaceIds via parsePlacesResponse,
// then splitOpenClosed, then add open results to seenPlaceIds.

export interface SplitResult { places: Place[]; closedPlaces: Place[] }

async function nearbySearchWithSplit(
  searchCoords: { lat: number; lng: number },
  includedPrimaryTypes: string[],
  excludedPrimaryTypes: string[],
  radius: number,
  transport: string,
  rankPreference: string,
  languageCode: string,
  seenPlaceIds: Set<string>,
  minRating: number,
  timeAvailable: string,
  includeClosedPlaces: boolean,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  const allPlaces = await performNearbySearch(
    searchCoords, includedPrimaryTypes, excludedPrimaryTypes, radius,
    transport, rankPreference, languageCode, seenPlaceIds, minRating,
    maxDistanceMeters, signal
  );
  const { open, closed } = splitOpenClosed(allPlaces, timeAvailable, includeClosedPlaces, transport);
  open.forEach(place => seenPlaceIds.add(place.id));
  return { places: open, closedPlaces: closed };
}

async function textSearchWithSplit(
  searchCoords: { lat: number; lng: number },
  textQuery: string,
  radius: number,
  transport: string,
  languageCode: string,
  seenPlaceIds: Set<string>,
  minRating: number,
  timeAvailable: string,
  includeClosedPlaces: boolean,
  openNow: boolean,
  includedType?: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  const allPlaces = await performTextSearch(
    searchCoords, textQuery, radius, transport, languageCode,
    seenPlaceIds, minRating, openNow, includedType, maxDistanceMeters, signal
  );
  const { open, closed } = splitOpenClosed(allPlaces, timeAvailable, includeClosedPlaces, transport);
  open.forEach(place => seenPlaceIds.add(place.id));
  return { places: open, closedPlaces: closed };
}

// ─── Merged dual search ─────────────────────────────────────────────────────

export async function performMergedDualSearch(
  searchCoords: { lat: number; lng: number },
  vibes: string[],
  transport: string,
  timeAvailable: string,
  location: string,
  categorySettings: CategorySettings | undefined,
  radius: number,
  seenPlaceIds: Set<string>,
  includeClosedPlaces: boolean,
  minRating: number,
  languageCode: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  console.log('Starting merged dual search (distance + popularity + text)...');

  const allTypes = new Set<string>();
  vibes.forEach(vibe => {
    const types = getTypesForVibeFromSettings(vibe, categorySettings);
    types.forEach(t => allTypes.add(t));
  });
  const includedPrimaryTypes = Array.from(allTypes);

  const textQuery = vibes.map(v => getTextSearchQuery(v, location)).join(' OR ');

  const settled = await Promise.allSettled([
    nearbySearchWithSplit(
      searchCoords, includedPrimaryTypes, [], radius, transport,
      'DISTANCE', languageCode, new Set<string>(), minRating,
      timeAvailable, includeClosedPlaces, maxDistanceMeters, signal
    ),
    nearbySearchWithSplit(
      searchCoords, includedPrimaryTypes, [], radius, transport,
      'POPULARITY', languageCode, new Set<string>(), minRating,
      timeAvailable, includeClosedPlaces, maxDistanceMeters, signal
    ),
    textSearchWithSplit(
      searchCoords, textQuery, radius, transport, languageCode,
      new Set<string>(), minRating, timeAvailable, includeClosedPlaces,
      !includeClosedPlaces, undefined, maxDistanceMeters, signal
    )
  ]);

  // One failed call among the three should not sink the search; all three failing should.
  const succeeded = settled.flatMap(r => (r.status === 'fulfilled' ? [r.value] : []));
  if (succeeded.length === 0) throw (settled[0] as PromiseRejectedResult).reason;
  if (signal?.aborted) throw new DOMException('Search superseded', 'AbortError');
  if (succeeded.length < settled.length) {
    console.warn(`Initial search: ${settled.length - succeeded.length} of ${settled.length} calls failed`, settled.filter(r => r.status === 'rejected'));
  }

  const combinedPlaces: Place[] = [];
  const combinedClosed: Place[] = [];
  const dualSeen = new Set<string>();

  succeeded.forEach(result => {
    result.places.forEach(place => {
      if (!seenPlaceIds.has(place.id) && !dualSeen.has(place.id)) {
        dualSeen.add(place.id);
        combinedPlaces.push(place);
      }
    });
    result.closedPlaces.forEach(place => {
      if (!seenPlaceIds.has(place.id) && !dualSeen.has(place.id)) {
        dualSeen.add(place.id);
        combinedClosed.push(place);
      }
    });
  });

  combinedPlaces.forEach(place => seenPlaceIds.add(place.id));
  console.log(`Merged dual search found ${combinedPlaces.length} unique places (${combinedClosed.length} closed)`);
  return { places: combinedPlaces, closedPlaces: combinedClosed };
}

// ─── Per-vibe search (kept as fallback; not used by default) ────────────────

export async function performPerVibeSearch(
  searchCoords: { lat: number; lng: number },
  vibes: string[],
  transport: string,
  timeAvailable: string,
  location: string,
  categorySettings: CategorySettings | undefined,
  radius: number,
  seenPlaceIds: Set<string>,
  includeClosedPlaces: boolean,
  minRating: number,
  languageCode: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  console.log(`Starting per-vibe parallel search for ${vibes.length} vibes...`);

  const perVibePromises = vibes.map(vibe => {
    const vibeTypes = getTypesForVibeFromSettings(vibe, categorySettings);
    const otherVibes = vibes.filter(v => v !== vibe);
    const excludedTypes = getExcludedTypesForVibe(vibe, otherVibes, categorySettings as unknown as Record<string, string[]>);

    const nearbyDistance = nearbySearchWithSplit(
      searchCoords, vibeTypes, excludedTypes, radius, transport,
      'DISTANCE', languageCode, new Set<string>(), minRating,
      timeAvailable, includeClosedPlaces, maxDistanceMeters, signal
    );
    const nearbyPopularity = nearbySearchWithSplit(
      searchCoords, vibeTypes, excludedTypes, radius, transport,
      'POPULARITY', languageCode, new Set<string>(), minRating,
      timeAvailable, includeClosedPlaces, maxDistanceMeters, signal
    );
    const textQuery = getTextSearchQuery(vibe, location);
    const textSearch = textSearchWithSplit(
      searchCoords, textQuery, radius, transport, languageCode,
      new Set<string>(), minRating, timeAvailable, includeClosedPlaces,
      !includeClosedPlaces, undefined, maxDistanceMeters, signal
    );

    return Promise.all([nearbyDistance, nearbyPopularity, textSearch]);
  });

  const allResults = await Promise.all(perVibePromises);

  const combinedPlaces: Place[] = [];
  const combinedClosed: Place[] = [];
  const dualSeen = new Set<string>();

  allResults.forEach(vibeResults => {
    vibeResults.forEach(result => {
      result.places.forEach(place => {
        if (!seenPlaceIds.has(place.id) && !dualSeen.has(place.id)) {
          dualSeen.add(place.id);
          combinedPlaces.push(place);
        }
      });
      result.closedPlaces.forEach(place => {
        if (!seenPlaceIds.has(place.id) && !dualSeen.has(place.id)) {
          dualSeen.add(place.id);
          combinedClosed.push(place);
        }
      });
    });
  });

  combinedPlaces.forEach(place => seenPlaceIds.add(place.id));
  console.log(`Per-vibe search found ${combinedPlaces.length} unique places (${combinedClosed.length} closed)`);
  return { places: combinedPlaces, closedPlaces: combinedClosed };
}

// ─── Initial search entry point ─────────────────────────────────────────────

export async function performInitialSearch(
  searchCoords: { lat: number; lng: number },
  vibes: string[],
  transport: string,
  timeAvailable: string,
  location: string,
  categorySettings: CategorySettings | undefined,
  radius: number,
  seenPlaceIds: Set<string>,
  includeClosedPlaces: boolean,
  minRating: number,
  languageCode: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  // Naked custom-vibe search: standard vibes array is empty. Building the
  // merged dual search with []-derived params produces `textQuery=""` (→
  // 400 Empty text_query) and an empty nearby-type list, which pollutes
  // the pool with unrelated places from a type-agnostic nearby call. For
  // custom-only searches, `searchCustomVibe` is the sole intended source.
  if (!vibes || vibes.length === 0) {
    return { places: [], closedPlaces: [] };
  }
  // Always use the merged approach — saves 3 API calls for 2-vibe searches
  // (was 6 calls with per-vibe, now 3 with merged). Per-vibe search is kept
  // as a fallback but no longer used by default.
  return performMergedDualSearch(
    searchCoords, vibes, transport, timeAvailable, location,
    categorySettings, radius, seenPlaceIds, includeClosedPlaces,
    minRating, languageCode, maxDistanceMeters, signal
  );
}

// ─── Category topup ─────────────────────────────────────────────────────────

export async function performCategoryTopup(
  searchCoords: { lat: number; lng: number },
  vibes: string[],
  transport: string,
  timeAvailable: string,
  _location: string,
  categorySettings: CategorySettings | undefined,
  radius: number,
  seenPlaceIds: Set<string>,
  includeClosedPlaces: boolean,
  minRating: number,
  languageCode: string,
  maxDistanceMeters?: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  console.log('Starting category topup searches (merged per-vibe)...');

  // Merge all types per vibe into a single call instead of individual type calls.
  // Previously: 4 vibes × 3 types = 12 API calls. Now: 1 per vibe = 4 calls max.
  const topupTasks: Promise<SplitResult>[] = [];
  for (const vibe of vibes) {
    const vibeTypes = getTypesForVibeFromSettings(vibe, categorySettings);
    if (vibeTypes.length === 0) continue;
    topupTasks.push(
      nearbySearchWithSplit(
        searchCoords, vibeTypes, [], radius, transport,
        'POPULARITY', languageCode, new Set<string>(), minRating,
        timeAvailable, includeClosedPlaces, maxDistanceMeters, signal
      )
    );
  }

  const topupResults = await Promise.all(topupTasks);

  const allTopupPlaces: Place[] = [];
  const allTopupClosed: Place[] = [];

  for (const result of topupResults) {
    for (const place of result.places) {
      if (!seenPlaceIds.has(place.id)) {
        seenPlaceIds.add(place.id);
        allTopupPlaces.push(place);
      }
    }
    for (const place of result.closedPlaces) {
      if (!seenPlaceIds.has(place.id)) {
        seenPlaceIds.add(place.id);
        allTopupClosed.push(place);
      }
    }
  }

  console.log(`Category topup complete: Added ${allTopupPlaces.length} places`);
  return { places: allTopupPlaces, closedPlaces: allTopupClosed };
}

// ─── H-204: Grid Find More ──────────────────────────────────────────────────

/**
 * Tier 1: Search 3 quadrant centers around the original search circle.
 * 3 parallel nearby calls → up to 60 new places.
 */
export async function performGridSearch(
  originalCenter: { lat: number; lng: number },
  vibes: string[],
  transport: string,
  timeAvailable: string,
  categorySettings: CategorySettings | undefined,
  radius: number,
  seenPlaceIds: Set<string>,
  includeClosedPlaces: boolean,
  minRating: number,
  languageCode: string,
  signal?: AbortSignal
): Promise<SplitResult> {
  const quadrantCenters = computeQuadrantCenters(originalCenter, radius);

  const allTypes = new Set<string>();
  vibes.forEach(vibe => {
    getTypesForVibeFromSettings(vibe, categorySettings).forEach(t => allTypes.add(t));
  });
  const includedPrimaryTypes = Array.from(allTypes);

  console.log(`[grid] Searching ${quadrantCenters.length} quadrants (R=${Math.round(radius)}m)`);

  const results = await Promise.all(
    quadrantCenters.map(qCenter =>
      nearbySearchWithSplit(
        qCenter, includedPrimaryTypes, [], radius, transport,
        'POPULARITY', languageCode, new Set<string>(), minRating,
        timeAvailable, includeClosedPlaces, undefined, signal
      )
    )
  );

  const combinedPlaces: Place[] = [];
  const combinedClosed: Place[] = [];
  const gridSeen = new Set<string>();

  for (const result of results) {
    for (const place of result.places) {
      if (!seenPlaceIds.has(place.id) && !gridSeen.has(place.id)) {
        gridSeen.add(place.id);
        combinedPlaces.push(place);
      }
    }
    for (const place of result.closedPlaces) {
      if (!seenPlaceIds.has(place.id) && !gridSeen.has(place.id)) {
        gridSeen.add(place.id);
        combinedClosed.push(place);
      }
    }
  }

  combinedPlaces.forEach(place => seenPlaceIds.add(place.id));
  console.log(`[grid] Found ${combinedPlaces.length} new places (${combinedClosed.length} closed)`);
  return { places: combinedPlaces, closedPlaces: combinedClosed };
}

/**
 * Tier 2: Broaden type list to generic popular categories.
 * 1 nearby call.
 */
export async function performBroaderTypeSearch(
  state: SearchState,
  signal?: AbortSignal
): Promise<SplitResult> {
  const broaderTypes = getBroaderTypes(state.vibes);
  if (broaderTypes.length === 0) return { places: [], closedPlaces: [] };

  return nearbySearchWithSplit(
    state.coords, broaderTypes, [], state.currentRadius,
    state.transport, 'POPULARITY', state.language,
    state.seenPlaceIds, state.minRating,
    state.timeAvailable, false, undefined, signal
  );
}

/**
 * Tier 3: Drop minRating to 0 to include all rated places.
 * 1 nearby call.
 */
export async function performRatingDropSearch(
  state: SearchState,
  signal?: AbortSignal
): Promise<SplitResult> {
  const allTypes = new Set<string>();
  state.vibes.forEach(vibe => {
    getTypesForVibeFromSettings(vibe, state.categorySettings).forEach(t => allTypes.add(t));
  });

  return nearbySearchWithSplit(
    state.coords, Array.from(allTypes), [], state.currentRadius,
    state.transport, 'POPULARITY', state.language,
    state.seenPlaceIds, 0,
    state.timeAvailable, false, undefined, signal
  );
}

// ─── Custom vibe search ─────────────────────────────────────────────────────

export async function searchCustomVibe(
  query: string,
  searchCoords: { lat: number; lng: number },
  radius: number,
  rating: number,
  seenIds: Set<string>,
  vibes: string[],
  customVibe: string,
  finalLocation: string,
  transport: string,
  timeAvailable: string,
  languageCode: string,
  maxRadius: number,
  signal?: AbortSignal
): Promise<SplitResult> {
  const absoluteMax = MAX_RADIUS_BY_TRANSPORT[transport] || 50000;
  const isCustomOnly = !vibes || vibes.length === 0 || vibes.every(v => v.startsWith('custom:'));

  // Scale thresholds by time budget — full-day trips need more results
  const timeThresholds: Record<string, number> = {
    '1-2-hrs': 3,
    'half-day': 5,
    'full-day': 8,
    '2-3-days': 10
  };
  const minResults = timeThresholds[timeAvailable] || 5;

  // Run query variants — start with primary, escalate if needed
  const primaryQuery = query;
  const variant2 = `${customVibe} activities near ${finalLocation}`;
  const variant3 = `${customVibe} center ${finalLocation}`;

  // Stage 1: Primary query at base radius
  // Don't send minRating or openNow to Google — custom vibes often match
  // places (parks, trails, dive centres) that lack ratings or opening hours.
  // We filter client-side more leniently after the fact.
  const s1Primary = await textSearchWithSplit(
    searchCoords, primaryQuery, radius, transport, languageCode,
    seenIds, 0, timeAvailable, true,
    false, undefined, radius, signal
  );
  // Only fire variant queries if primary returned few results
  let s1 = s1Primary;
  if (s1Primary.places.length < minResults) {
    const [s1v2, s1v3] = await Promise.all([
      textSearchWithSplit(
        searchCoords, variant2, radius, transport, languageCode,
        seenIds, 0, timeAvailable, true,
        false, undefined, radius, signal
      ),
      textSearchWithSplit(
        searchCoords, variant3, radius, transport, languageCode,
        seenIds, 0, timeAvailable, true,
        false, undefined, radius, signal
      )
    ]);
    s1 = {
      places: [...s1Primary.places, ...s1v2.places, ...s1v3.places],
      closedPlaces: [...s1Primary.closedPlaces, ...s1v2.closedPlaces, ...s1v3.closedPlaces]
    };
  }
  // For custom-only searches, always continue to stage 2 for more coverage
  if (!isCustomOnly && s1.places.length >= minResults) return s1;

  // Stage 2: Expanded radius (4x), escalating variants
  const expandedRadius = Math.min(radius * 4, maxRadius);
  const s2Primary = await textSearchWithSplit(
    searchCoords, primaryQuery, expandedRadius, transport, languageCode,
    seenIds, 0, timeAvailable, true,
    false, undefined, expandedRadius, signal
  );
  let s2All = s2Primary;
  if (s1.places.length + s2Primary.places.length < minResults) {
    const [s2v2, s2v3] = await Promise.all([
      textSearchWithSplit(
        searchCoords, variant2, expandedRadius, transport, languageCode,
        seenIds, 0, timeAvailable, true,
        false, undefined, expandedRadius, signal
      ),
      textSearchWithSplit(
        searchCoords, variant3, expandedRadius, transport, languageCode,
        seenIds, 0, timeAvailable, true,
        false, undefined, expandedRadius, signal
      )
    ]);
    s2All = {
      places: [...s2Primary.places, ...s2v2.places, ...s2v3.places],
      closedPlaces: [...s2Primary.closedPlaces, ...s2v2.closedPlaces, ...s2v3.closedPlaces]
    };
  }
  const combined = {
    places: [...s1.places, ...s2All.places],
    closedPlaces: [...s1.closedPlaces, ...s2All.closedPlaces]
  };
  if (!isCustomOnly && combined.places.length >= minResults) return combined;

  // Stage 3: Maximum radius (10x), widest possible net
  const maxExpandedRadius = Math.min(radius * 10, absoluteMax);
  const r3 = await textSearchWithSplit(
    searchCoords, query, maxExpandedRadius, transport, languageCode,
    seenIds, 0, timeAvailable, true,
    false, undefined, maxExpandedRadius, signal
  );
  const allTextResults = {
    places: [...combined.places, ...r3.places],
    closedPlaces: [...combined.closedPlaces, ...r3.closedPlaces]
  };

  // Stage 4: Type-inference nearby search — use Google's own type
  // classification from text search results to find more of the same kind.
  // Skip if we already have plenty of results (saves 2 API calls).
  if (allTextResults.places.length >= 10) {
    return { places: allTextResults.places, closedPlaces: allTextResults.closedPlaces };
  }
  // Extract the most common non-generic types from results found so far.
  const genericTypes = new Set(['point_of_interest', 'establishment', 'store', 'place_of_worship', 'locality', 'political']);
  const typeCounts = new Map<string, number>();
  for (const p of allTextResults.places) {
    for (const t of p.types) {
      if (!genericTypes.has(t)) {
        typeCounts.set(t, (typeCounts.get(t) || 0) + 1);
      }
    }
  }
  // Pick types that appear on 2+ results (or all types if few results)
  const minTypeCount = allTextResults.places.length >= 4 ? 2 : 1;
  const inferredTypes = [...typeCounts.entries()]
    .filter(([, count]) => count >= minTypeCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([type]) => type);

  if (inferredTypes.length > 0) {
    const nearbyRadius = Math.min(radius * 6, absoluteMax);
    const [distResult, popResult] = await Promise.all([
      nearbySearchWithSplit(
        searchCoords, inferredTypes, [], nearbyRadius,
        transport, 'DISTANCE', languageCode,
        seenIds, Math.max(0, rating / 2), timeAvailable,
        true, nearbyRadius, signal
      ),
      nearbySearchWithSplit(
        searchCoords, inferredTypes, [], nearbyRadius,
        transport, 'POPULARITY', languageCode,
        seenIds, Math.max(0, rating / 2), timeAvailable,
        true, nearbyRadius, signal
      )
    ]);
    allTextResults.places.push(...distResult.places, ...popResult.places);
    allTextResults.closedPlaces.push(...distResult.closedPlaces, ...popResult.closedPlaces);
  }

  return {
    places: allTextResults.places,
    closedPlaces: allTextResults.closedPlaces
  };
}

// ─── Load more (internal) ───────────────────────────────────────────────────

export async function loadMoreInternal(
  state: SearchState,
  includeClosedPlaces: boolean,
  signal?: AbortSignal
): Promise<{ places: Place[]; newMinRating: number; reachedLimit: boolean }> {
  const MAX_ITERATIONS = 10;

  let iterationCount = 0;
  let allNewPlaces: Place[] = [];
  let triedIncludingClosed = includeClosedPlaces;
  let triedBroaderTypes = false;

  while (iterationCount < MAX_ITERATIONS) {
    iterationCount++;
    state.loadMoreAttempts += 1;

    const newRadius = Math.min(state.currentRadius + state.radiusStep, state.maxRadius);
    const atMaxRadius = newRadius >= state.maxRadius;

    if (iterationCount % 3 === 0 && state.minRating > 0) {
      state.minRating = Math.max(0, state.minRating - 1);
    }
    const atMinRating = state.minRating === 0;

    const reachedAbsoluteLimit = atMaxRadius && atMinRating && triedIncludingClosed && triedBroaderTypes;

    const allTypes = new Set<string>();
    state.vibes.forEach(vibe => {
      getTypesForVibeFromSettings(vibe, state.categorySettings).forEach(t => allTypes.add(t));
    });

    const result = await nearbySearchWithSplit(
      state.coords,
      Array.from(allTypes),
      [],
      newRadius,
      state.transport,
      'POPULARITY',
      state.language,
      state.seenPlaceIds,
      state.minRating,
      state.timeAvailable,
      triedIncludingClosed,
      undefined,
      signal
    );

    state.currentRadius = newRadius;

    if (result.places.length > 0) {
      allNewPlaces = [...allNewPlaces, ...result.places];
      return { places: allNewPlaces, newMinRating: state.minRating, reachedLimit: reachedAbsoluteLimit };
    }

    if (!triedIncludingClosed && result.closedPlaces.length > 0) {
      triedIncludingClosed = true;
      continue;
    }

    if (!triedBroaderTypes) {
      triedBroaderTypes = true;
      const broaderTypesList = getBroaderTypes(state.vibes);
      if (broaderTypesList.length > 0) {
        const broaderResult = await nearbySearchWithSplit(
          state.coords,
          broaderTypesList,
          [],
          newRadius,
          state.transport,
          'POPULARITY',
          state.language,
          state.seenPlaceIds,
          state.minRating,
          state.timeAvailable,
          triedIncludingClosed,
          undefined,
          signal
        );

        if (broaderResult.places.length > 0) {
          allNewPlaces = [...allNewPlaces, ...broaderResult.places];
          return { places: allNewPlaces, newMinRating: state.minRating, reachedLimit: reachedAbsoluteLimit };
        }
      }
    }

    if (reachedAbsoluteLimit) {
      return { places: [], newMinRating: state.minRating, reachedLimit: true };
    }
  }

  return { places: [], newMinRating: state.minRating, reachedLimit: true };
}

export function canLoadMoreInternal(state: SearchState | null): boolean {
  if (!state) return false;
  return !(state.currentRadius >= state.maxRadius && state.minRating === 0);
}
